/**
 * ContentOverlay · iter168 (2026-02-27)
 *
 * Fullscreen-Overlay mit responsivem Text (Seitenbreite je Viewport) und X zum Schließen.
 * Dient sowohl für Static-Pages (Impressum/AGB/Datenschutz) als auch für News/FAQ-Listen.
 *
 * Props:
 *   - title (string)
 *   - onClose (() => void)
 *   - children (ReactNode) — vorgerenderter Inhalt (siehe StaticPageContent + ArticleListContent)
 */
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";

export function ContentOverlay({ title, onClose, children, testId = "content-overlay", variant = "prose" }) {
  const { t } = useTranslation();
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // iter169 · variant="feed" nutzt Container-Breite wie mittlere Feed-Spalte (max-w-xl),
  // variant="prose" bleibt bei max-w-3xl für lange Rechtstexte.
  const contentMaxW = variant === "feed"
    ? "max-w-full sm:max-w-xl"
    : "max-w-full sm:max-w-2xl lg:max-w-3xl";
  const bg = variant === "feed" ? "bg-[#FAFAFA]" : "bg-white";

  return (
    <div data-testid={testId} className={`fixed inset-0 z-[90] ${bg} overflow-y-auto`}>
      <div className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-[#E5E5E5] px-4 py-3 flex items-center justify-between">
        <h2 className="text-base sm:text-lg font-bold text-[#0A0A0A] truncate">{title}</h2>
        <button data-testid={`${testId}-close`} onClick={onClose}
          className="p-2 -mr-2 rounded-full hover:bg-[#F5F5F5]" aria-label={t("common.close_aria")}>
          <X size={20} />
        </button>
      </div>
      {/* Seitenbreite responsiv */}
      <div className={`mx-auto px-4 sm:px-6 py-6 ${contentMaxW}`}>
        {children}
      </div>
    </div>
  );
}

// ─── Static Page Content (Impressum, AGB, Privacy) ───────────────
export function StaticPageContent({ body }) {
  return (
    <article
      data-testid="static-page-body"
      className="prose prose-neutral max-w-none prose-sm sm:prose-base
                 prose-headings:font-bold prose-headings:text-[#0A0A0A]
                 prose-a:text-[#0A0A0A] prose-a:underline"
      dangerouslySetInnerHTML={{ __html: body || "<p>Kein Inhalt hinterlegt.</p>" }}
    />
  );
}

// ─── Article List Content (News, FAQ) ────────────────────────────
export function ArticleListContent({ items }) {
  const { t } = useTranslation();
  if (!items || items.length === 0) {
    return <div className="text-sm text-[#666] text-center py-12">{t("common.no_entries")}</div>;
  }
  return (
    <div className="space-y-6">
      {items.map((a) => (
        <article
          key={a.article_id}
          data-testid={`article-view-${a.article_id}`}
          className="pb-6 border-b border-[#E5E5E5] last:border-b-0"
        >
          <h3 className="text-base sm:text-lg font-bold text-[#0A0A0A] mb-2">{a.title}</h3>
          <div className="text-xs text-[#999] mb-3">
            {new Date(a.updated_at || a.created_at).toLocaleDateString("de-DE", {
              day: "2-digit", month: "long", year: "numeric",
            })}
          </div>
          <div
            className="prose prose-neutral max-w-none prose-sm
                       prose-headings:font-bold prose-headings:text-[#0A0A0A]
                       prose-a:text-[#0A0A0A] prose-a:underline"
            dangerouslySetInnerHTML={{ __html: a.body || "" }}
          />
        </article>
      ))}
    </div>
  );
}
